<template>
  <div class="sf-full-width tw-relative" :class="floatingLabelWrapperClass">
    <label
      v-if="label"
      :for="labelFor"
      class="sf-input__label !tw-text-sf-gray-dark"
      :class="floatingLabelClasses"
    >
      {{ label }}
      <span v-if="required" class="sf-c-danger-light-1">
        <abbr title="required">*</abbr>
      </span>
    </label>
    <div class="sf-input__container">
      <slot>
        <SfInputBase
          v-model="value"
          :align="align"
          :component-type="componentType"
          :focused="focused"
          :has-error="hasError"
          :hide-browser-appearance="hideBrowserAppearance"
          :error-text="errorText"
          :helper-text="helperText"
          :placeholder="placeholderText"
          :disabled="disabled"
          :right-padding="rightElementWidth"
          v-bind="$attrs"
          @focus="handleFocus"
          @blur="handleBlur"
        />
      </slot>
      <div ref="rightElement" class="sf-input__right-element">
        <slot name="right-element" />
      </div>
    </div>

    <div v-if="helperText" class="sf-form-hint">
      {{ helperText }}
    </div>
    <div v-if="errors.length > 0" :class="errorContainerClasses">
      <SfInputError v-for="e in errors" :key="e">{{ e }}</SfInputError>
    </div>
  </div>
</template>
<script setup lang="ts" generic="MV">
import { computed, ref, toRef, useAttrs, watchEffect } from 'vue'
import { useVModel } from '@vueuse/core'

import SfInputBase from './sf-input-base.vue'
import SfInputError from './sf-input-error.vue'
import useError from './use-error'

defineOptions({
  name: 'SfInputText',
  inheritAttrs: false,
})

interface Props {
  align?: 'left' | 'center' | 'right'
  charLimit?: number
  componentType?: 'input' | 'textarea'
  focused?: boolean | number
  hasError?: boolean
  hideBrowserAppearance?: boolean
  disabled?: boolean
  errorText?: string | string[]
  errorStyle?: 'default' | 'seamless'
  helperText?: string
  label?: string
  modelValue?: MV
  placeholder?: string
  required?: boolean
  seamlessErrors?: boolean
  small?: boolean
  hasFloatingLabel?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  align: 'left',
  charLimit: 0,
  componentType: 'input',
  focused: 0,
  errorText: () => [],
  errorStyle: 'default',
  helperText: '',
  label: '',
  modelValue: undefined,
  placeholder: '',
  hasFloatingLabel: false,
})

const $attrs = useAttrs()

const rightElement = ref<HTMLElement | null>(null)
const rightElementWidth = ref<`${string}px` | ''>('')

const emit =
  defineEmits<{
    'update:modelValue': [value: string | undefined]
  }>()

const value = useVModel(props, 'modelValue', emit)

const errors = useError(toRef(props, 'errorText'))
const isFocused = ref(false)

const floatingLabelClasses = computed(() => {
  if (!props.hasFloatingLabel || props.componentType !== 'input') {
    return ''
  }

  const floatingLabelStyling = `
        tw-bg-white
        !tw-text-[12px]
        !tw-font-normal
        tw-absolute
        tw-duration-100
        tw-ease-linear
        tw-z-10
        tw-pointer-events-none`
  return !isFocused.value && !value.value
    ? floatingLabelStyling +
        `
        tw--translate-y-[-6px]
        tw--translate-x-[-8px]
        !tw-text-sf-gray-light
        tw-italic`
    : floatingLabelStyling +
        `
        tw-pl-1
        tw--translate-y-[0px]
        tw--translate-x-[-5px]
        !tw-text-sf-gray-darker
        !tw-leading-[1px]`
})

const errorContainerClasses = computed(() => {
  return props.errorStyle === 'seamless'
    ? 'tw-absolute tw-bottom-0 tw-left-0 tw-translate-y-full'
    : ''
})

const labelFor = computed<string>(() => {
  return $attrs?.id && typeof $attrs.id === 'string' ? $attrs.id : ''
})

const placeholderText = computed(() => {
  return props.hasFloatingLabel ? '' : props.placeholder
})

const floatingLabelWrapperClass = computed(() => {
  return props.hasFloatingLabel ? 'tw-relative' : ''
})

watchEffect(() => {
  if (!rightElement.value) {
    return
  }

  rightElementWidth.value = `${rightElement.value.offsetWidth + 8}px`
})

const handleFocus = () => {
  isFocused.value = true
}

const handleBlur = () => {
  isFocused.value = false
}
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.sf-form-error,
.sf-form-hint {
  margin: 8px 0 0;
}

.sf-input {
  &__container {
    position: relative;
  }

  &__label {
    @include color('text', 'darker');

    font-size: 14px;
    font-weight: 700;
    line-height: 16.8px;
    margin: 0 0 space(1) 0;
  }

  &__right-element {
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: absolute;
    right: space(1);
    top: 0;
    bottom: 0;

    &:empty {
      display: none;
    }
  }
}
</style>
